/**
 * Laender: intern IMMER ISO 3166-1 alpha-2 in Grossbuchstaben ('DE').
 * DHL will alpha-3 ('DEU'); die Umsetzung passiert nur im Carrier-Adapter.
 *
 * EU-Liste nach dem Muster von bbd `packages/regeln/src/steuer.ts`
 * (Herkunft: int1183_bbd, origin/main, Stand 2026-09-03). GB bewusst nicht dabei.
 */
export const EU_LAENDER = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
] as const;
export type EuLand = (typeof EU_LAENDER)[number];
export const EU_LAENDER_STAND = '2026-10-17';

export function istEuLand(land: string): land is EuLand {
  return (EU_LAENDER as readonly string[]).includes(land.toUpperCase());
}

/**
 * Amtlich zugewiesene Codes nach ISO 3166-1 alpha-2 (249, Stand 2026-10-17). Bewusst eine
 * explizite Liste statt `Intl.DisplayNames`: das kennt auch Sammel- und Pseudocodes (EU, UN,
 * XK, QO …), und ein solcher Code darf nie als „Drittland, steuerfreie Ausfuhr“ durchgehen (W3).
 */
export const ISO_LAENDER: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE',
  'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD',
  'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
  'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
  'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME',
  'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
  'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI',
  'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK',
  'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];
const ISO_SET = new Set(ISO_LAENDER);

export function istLandcode(land: string): boolean {
  return ISO_SET.has(land);
}

/** alpha-2 → alpha-3 fuer die Laender, die im Versand vorkommen koennen (EU + haeufige Drittlaender). */
const ALPHA3: Record<string, string> = {
  AT: 'AUT', BE: 'BEL', BG: 'BGR', CH: 'CHE', CY: 'CYP', CZ: 'CZE', DE: 'DEU', DK: 'DNK',
  EE: 'EST', ES: 'ESP', FI: 'FIN', FR: 'FRA', GB: 'GBR', GR: 'GRC', HR: 'HRV', HU: 'HUN',
  IE: 'IRL', IT: 'ITA', LI: 'LIE', LT: 'LTU', LU: 'LUX', LV: 'LVA', MT: 'MLT', NL: 'NLD',
  NO: 'NOR', PL: 'POL', PT: 'PRT', RO: 'ROU', SE: 'SWE', SI: 'SVN', SK: 'SVK', US: 'USA',
  CA: 'CAN', AU: 'AUS', NZ: 'NZL', JP: 'JPN',
};

export function alpha3(land: string): string {
  const a3 = ALPHA3[land.toUpperCase()];
  if (!a3) throw new Error(`Kein alpha-3-Code hinterlegt fuer ${land}`);
  return a3;
}
